import React, { useMemo } from "react";
import { useField } from "formik";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Input } from "../Input";
import { twMerge } from "tailwind-merge";
import { isBefore, isToday, addMinutes } from "date-fns";

export interface Props {
  name: string;
  label?: string;
  showTimeSelect?: boolean;
  tooltipText?: string;
  calendarClassName?: string;
  disabled?: boolean;
  labelClassName?: string;
  className?: string;
  placeholder?: string;
  minTime?: Date;
  handleOnChange?: () => void;
}

const DatePicker = ({
  name,
  label,
  showTimeSelect = false,
  tooltipText,
  calendarClassName,
  disabled,
  labelClassName,
  className,
  placeholder,
  minTime: propsMinTime,
  handleOnChange,
}: Props) => {
  const [field, meta, helpers] = useField<Date>(name);

  const { value, error, touched } = meta;
  const { setValue } = helpers;

  const minTime = useMemo(() => {
    if (!(value instanceof Date)) {
      return Date.now();
    }

    if (!isToday(value)) {
      return new Date().setHours(0, 0);
    }

    return propsMinTime?.getTime() || Date.now();
  }, [propsMinTime, value]);

  const isError = !!touched && !!error;
  return (
    <ReactDatePicker
      {...field}
      disabled={disabled}
      timeIntervals={15}
      minDate={new Date()}
      minTime={minTime}
      maxTime={new Date(new Date().setHours(23, 59, 0, 0))}
      dateFormat={`yyyy/MM/dd ${showTimeSelect ? "hh:mm" : ""}`}
      selected={value}
      onChange={(date: Date) => {
        handleOnChange && handleOnChange();
        if (isBefore(date, Date.now())) {
          setValue(addMinutes(new Date(Date.now()), 15));
        } else {
          setValue(date);
        }
      }}
      onKeyDown={(e: any) => {
        e.preventDefault();
      }}
      showTimeSelect={showTimeSelect}
      calendarClassName={twMerge(
        "min-w-[400px] right-[200px] absolute z-999",
        calendarClassName,
      )}
      popperPlacement="top-start"
      popperModifiers={[
        {
          name: "arrow",
          options: {
            element: null,
          },
        },
      ]}
      placeholderText={placeholder}
      customInput={
        <Input
          label={label}
          labelClassName={twMerge("endemic:text-invert", labelClassName)}
          alignCenter={false}
          tooltipText={tooltipText}
          value={field.value?.toString()}
          className={twMerge(
            "endemic:text-invert focus:endemic:border-green",
            className,
          )}
          isError={isError}
          errorMsg={error}
        />
      }
    />
  );
};

export default DatePicker;
